import { lazy, Suspense, useRef } from 'react';
import { CanvasStage } from './CanvasStage';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorBoundary } from './ErrorBoundary';
import { useAutosave } from './hooks/useAutosave';
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts';
import { useImageImport } from './hooks/useImageImport';
import { useTheme } from './hooks/useTheme';
import { MenuBar } from './MenuBar';
import { RightPanel } from './RightPanel';
import { StatusBar } from './StatusBar';
import { Toast } from './Toast';
import { ToolOptions } from './ToolOptions';
import { ToolRail } from './ToolRail';
import { UpdateBanner } from './UpdateBanner';

/**
 * Chrome you open, as opposed to chrome that is already on screen.
 *
 * Each of these is behind an explicit action — a shortcut, a menu item — so none
 * of them belongs in the chunk that has to load before the editor appears. The
 * store already knows a dialog should be open by the time its chunk lands, so
 * the fallback is simply "not yet".
 */
const ExportDialog = lazy(() =>
  import('./ExportDialog').then((m) => ({ default: m.ExportDialog })),
);
const BatchExportDialog = lazy(() =>
  import('./BatchExportDialog').then((m) => ({ default: m.BatchExportDialog })),
);
const ResizeDialog = lazy(() =>
  import('./ResizeDialog').then((m) => ({ default: m.ResizeDialog })),
);
const ShortcutsDialog = lazy(() =>
  import('./ShortcutsDialog').then((m) => ({ default: m.ShortcutsDialog })),
);
const AboutDialog = lazy(() => import('./AboutDialog').then((m) => ({ default: m.AboutDialog })));
const CommandPalette = lazy(() =>
  import('./CommandPalette').then((m) => ({ default: m.CommandPalette })),
);

/**
 * Editor chrome: menu bar on top, tool rail on the left, drawing area in the
 * middle, panels on the right, status bar at the bottom.
 */
export function AppShell() {
  const stageRef = useRef<HTMLDivElement | null>(null);

  useGlobalShortcuts();
  useTheme();
  useAutosave();
  useImageImport(stageRef);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-app" data-testid="app-shell">
      <MenuBar />
      <ToolOptions />
      <div className="flex min-h-0 flex-1">
        <ToolRail />
        <ErrorBoundary>
          <CanvasStage stageRef={stageRef} />
        </ErrorBoundary>
        <RightPanel />
      </div>
      <StatusBar />
      <Toast />
      <Suspense fallback={null}>
        <ShortcutsDialog />
      </Suspense>
      <Suspense fallback={null}>
        <ExportDialog />
      </Suspense>
      <Suspense fallback={null}>
        <BatchExportDialog />
      </Suspense>
      <Suspense fallback={null}>
        <ResizeDialog />
      </Suspense>
      <ConfirmDialog />
      <Suspense fallback={null}>
        <AboutDialog />
      </Suspense>
      <Suspense fallback={null}>
        <CommandPalette />
      </Suspense>
      <UpdateBanner />
    </div>
  );
}
